import { useCallback, useEffect, useRef, useState } from 'react'
import { createContainer } from 'unstated-next'

export type ScrollDirection = 'up' | 'down'

interface useScrollProps {
  direction: ScrollDirection
  register: (
    element: HTMLElement | null,
    events?: ScrollElementObserverEvents,
    options?: Partial<ScrollElementObserverOptions>
  ) => () => void
}

interface ScrollElementObserverEvents {
  enter?: (direction: ScrollDirection, y: number) => void
  leave?: (direction: ScrollDirection, y: number) => void
  active?: (y: number) => void
}

const ACTIVE_PX = window.innerWidth < 600 ? 200 : 150

interface ScrollElementObserverOptions {
  debug: boolean
  enterOffset: number
}

const defaultScrollElementObserverOptions = {
  debug: false,
  enterOffset: 0,
}

class ScrollElementObserver {
  disable: boolean = false
  limit!: [number, number]
  limitTrigger!: [number, number]
  isYinElement = false
  private active = false
  private canActiveChange = false
  private readonly debug
  private readonly enterOffset

  constructor(
    readonly element: HTMLElement,
    public events: ScrollElementObserverEvents = {},
    options: Partial<ScrollElementObserverOptions> = {}
  ) {
    this.updateDimension()
    setTimeout(() => {
      this.canActiveChange = true
    }, 20)
    const op = {
      ...defaultScrollElementObserverOptions,
      ...options,
    }
    this.debug = op.debug
    this.enterOffset = op.enterOffset
  }

  updateDimension(): void {
    const rect = this.element.getBoundingClientRect()
    const y = rect.top + window.scrollY
    this.limit = [Math.round(y), Math.round(y + rect.height)]
    this.limitTrigger = [
      Math.round(this.limit[0] - window.innerHeight / 2),
      Math.round(this.limit[1] - window.innerHeight / 2),
    ]
  }

  checkActive(y: number = window.scrollY): void {
    if (this.active) return
    if (!this.canActiveChange) return
    if (this.limit[0] - window.innerHeight + ACTIVE_PX - y < 0) {
      this.active = true
      if (this.events.active) this.events.active(y)
    }
  }

  private nowIsYinElement(y: number = window.scrollY): boolean {
    return (
      y > this.limitTrigger[0] - this.enterOffset &&
      y <= this.limitTrigger[1] + this.enterOffset
    )
  }

  check(direction: ScrollDirection, y: number = window.scrollY): void {
    if (this.disable) return
    const { leave, enter } = this.events
    this.checkActive(y)
    const nowIsYinElement = this.nowIsYinElement()
    if (this.isYinElement === nowIsYinElement) return
    if (this.isYinElement && !nowIsYinElement) {
      if (leave) leave(direction, y)
    } else if (!this.isYinElement && nowIsYinElement) {
      if (enter) enter(direction, y)
    }
    this.isYinElement = nowIsYinElement
  }
}

function useScroll(): useScrollProps {
  const y = useRef(0)
  const [observers, setObservers] = useState<ScrollElementObserver[]>([])
  const [direction, setDirection] = useState<ScrollDirection>('down')

  useEffect(() => {
    const hook = (): void => {
      const { scrollY } = window
      let d: ScrollDirection = 'up'
      if (y.current < scrollY) {
        d = 'down'
      }
      y.current = scrollY
      setDirection(d)
      observers.forEach((ob) => ob.check(d, y.current))
    }
    window.addEventListener('scroll', hook, false)
    return () => {
      window.removeEventListener('scroll', hook)
    }
  }, [observers])

  const register = useCallback(
    (
      element: HTMLElement | null,
      events: ScrollElementObserverEvents = {},
      options: Partial<ScrollElementObserverOptions> = {}
    ) => {
      const unregister = (): void => {
        if (!element) return
        const ex = observers.find((ob) => ob.element === element)
        if (ex) ex.disable = true
      }
      if (!element) return unregister
      const ex = observers.find((ob) => ob.element === element)
      if (ex) {
        // ex.updateDimension()
        ex.disable = false
        ex.events = events
      } else {
        setObservers([
          ...observers,
          new ScrollElementObserver(element, events, options),
        ])
      }
      return unregister
    },
    [observers]
  )

  return {
    direction,
    register,
  }
}

const Scroll = createContainer(useScroll)

export default Scroll
